/**
 * Client table component
 */

import React, { useState, useRef } from 'react';
import { getTierColor } from '../../services/TierColorService';
import { getYearSpecificData } from '../../utils/yearSpecificUtils';
import { formatCurrency } from '../../services/RevenueCalculationService';
import '../../styles/shared/table.css';
import '../../styles/shared/buttons.css';

/**
 * Resizable table component for displaying client data
 * @param {Object} props - Component props
 * @param {Array} props.clients - Array of client objects to display
 * @param {Array} props.columns - Column configuration objects
 * @param {Function} props.unpinClient - Function to unpin a client
 * @param {Function} props.isClientLocked - Function to check if client is locked
 * @param {Function} props.toggleClientLock - Function to toggle client lock state
 * @param {Function} props.isTableRowExpanded - Function to check if a row is expanded
 * @param {Function} props.toggleTableRowExpansion - Function to toggle row expansion
 * @param {Function} props.isExternalClient - Function to check if client is external
 * @param {Boolean} props.useUniformColor - Whether to use uniform color scheme
 * @param {String} props.selectedYear - The selected year for history mode
 * @returns {JSX.Element} ClientTable component
 */
const ClientTable = ({ 
  clients, 
  columns, 
  unpinClient, 
  isClientLocked, 
  toggleClientLock,
  isTableRowExpanded,
  toggleTableRowExpansion,
  isExternalClient,
  useUniformColor,
  selectedYear
}) => {
  const [columnWidths, setColumnWidths] = useState(
    columns.reduce((acc, col) => {
      acc[col.key] = col.defaultWidth || 150;
      return acc;
    }, {})
  );
  
  const [isResizing, setIsResizing] = useState(false);
  const [resizingColumn, setResizingColumn] = useState(null);
  const [draggedItem, setDraggedItem] = useState(null);
  const tableRef = useRef(null);

  // Uniform color for all cards when toggle is active
  const uniformColor = '#0074D9';

  // Handle column resize
  const handleMouseDown = (e, columnKey) => {
    e.preventDefault();
    setIsResizing(true);
    setResizingColumn(columnKey);
    
    const startX = e.clientX;
    const startWidth = columnWidths[columnKey];
    
    const handleMouseMove = (e) => {
      const deltaX = e.clientX - startX;
      const newWidth = Math.max(80, startWidth + deltaX); // Minimum width of 80px
      
      setColumnWidths(prev => ({
        ...prev,
        [columnKey]: newWidth
      }));
    };
    
    const handleMouseUp = () => {
      setIsResizing(false);
      setResizingColumn(null);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
    
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  };

  // Prepare table data with expanded rows
  const prepareTableData = () => {
    const tableData = [];
    
    clients.forEach((client, index) => {
      // Add the main client row
      tableData.push({
        ...client,
        className: `${draggedItem?.index === index ? 'dragging' : ''} ${isExternalClient(client) ? 'external-client' : ''}`.trim(),
        isMainRow: true,
        originalIndex: index
      });
      
      // Add expanded row if this client is expanded
      if (isTableRowExpanded(client)) {
        const clientTier = client['Tier'] || 'Unknown';
        const cardColor = useUniformColor ? uniformColor : getTierColor(clientTier, 'dark');
        const cardLightColor = useUniformColor ? '#f5f8ff' : getTierColor(clientTier, 'light');
        
        // Get revenue to display (conference-specific or regular)
        const displayRevenue = client.isConferenceRevenueActive 
          ? formatCurrency(client.conferenceRevenue)
          : getYearSpecificData(client, selectedYear).revenue;
        
        tableData.push({
          ...client,
          className: 'expanded-row',
          isMainRow: false,
          isExpandedRow: true,
          expandedData: {
            cardColor,
            cardLightColor,
            displayRevenue
          }
        });
      }
    });

    return tableData;
  };
  
  // Render table row data
  const renderTableRowData = (client, columnKey, index) => {
    // Handle expanded row content
    if (client.isExpandedRow) {
      if (columnKey === 'client_name') {
        // Get year-specific data for the expanded row
        const expandedYearData = getYearSpecificData(client, selectedYear);
        
        // Render the expanded content spanning all columns
        return (
          <div className="table__expanded-row-content">
            <div 
              className="table__expanded-header"
              style={{ 
                backgroundColor: client.expandedData.cardLightColor,
                borderBottom: `1px solid ${client.expandedData.cardColor}40`
              }}
            >
              <h4 style={{ color: client.expandedData.cardColor, margin: 0 }}>
                Additional Details - {client.client_name}
              </h4>
            </div>
            <div className="table__expanded-body">
              <div className="table__expanded-details-grid">
                <div className="table__expanded-detail-section">
                  <h5>{selectedYear === 'All' ? 'Total' : `${selectedYear} Data`}</h5>
                  <div className="table__detail-row">
                    <span className="table__detail-label">Rooms:</span>
                    <span className="table__detail-value">{expandedYearData.rooms || 'N/A'}</span>
                  </div>
                  <div className="table__detail-row">
                    <span className="table__detail-label">Room Nights:</span>
                    <span className="table__detail-value">{expandedYearData.roomNights || 'N/A'}</span>
                  </div>
                </div>
                
                <div className="table__expanded-detail-section">
                  <h5>Financial Information</h5>
                  <div className="table__detail-row">
                    <span className="table__detail-label">{selectedYear === 'All' ? 'Total Revenue' : `${selectedYear} Revenue`}:</span>
                    <span className="table__detail-value revenue">{client.expandedData.displayRevenue}</span>
                  </div>
                  <div className="table__detail-row">
                    <span className="table__detail-label">Last Booking:</span>
                    <span className="table__detail-value last-booking">{client['Last Booking Revenue'] || 'N/A'}</span>
                  </div>
                </div>
                
                <div className="table__expanded-detail-section">
                  <h5>Classification</h5>
                  <div className="table__detail-row">
                    <span className="table__detail-label">Tier:</span>
                    <span className="table__detail-value">{client['Tier'] || 'N/A'}</span>
                  </div>
                  <div className="table__detail-row">
                    <span className="table__detail-label">LB Conference:</span>
                    <span className="table__detail-value">{client['Segment'] || 'N/A'}</span>
                  </div>
                </div>
                
                <div className="table__expanded-detail-section">
                  <h5>Management</h5>
                  <div className="table__detail-row">
                    <span className="table__detail-label">Current Manager:</span>
                    <span className="table__detail-value">{client.conference_manager}</span>
                  </div>
                  <div className="table__detail-row">
                    <span className="table__detail-label">Time Zone:</span>
                    <span className="table__detail-value">{client['Time zone'] || 'N/A'}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      } else {
        // For other columns in expanded row, return empty content
        return null;
      }
    }

    // Handle regular row content
    const clientTier = client['Tier'] || 'Unknown';
    const cardColor = useUniformColor ? uniformColor : getTierColor(clientTier, 'dark');
    const isExternal = isExternalClient(client);
    
    switch (columnKey) {
      case 'client_name':
        return (
          <div className="table__client-name">
            <div 
              className="table__drag-handle--expandable" 
              title={isTableRowExpanded(client) ? "Click to collapse details" : "Click to expand details"}
              onClick={(e) => {
                e.stopPropagation();
                toggleTableRowExpansion(client, index);
              }}
            >
              {isTableRowExpanded(client) ? (
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="18 15 12 9 6 15"></polyline>
                </svg>
              ) : (
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="6 9 12 15 18 9"></polyline>
                </svg>
              )}
            </div>
            <div className="table__color-indicator" style={{ backgroundColor: cardColor }}></div>
            <span title={client.client_name}>{client.client_name}</span>
            {isExternal && (
              <span 
                className="table__external-badge" 
                title={`Added from ${client.conference_manager}`}
                style={{
                  backgroundColor: useUniformColor ? uniformColor : cardColor
                }}
              >
                {client.conference_manager}
              </span>
            )}
          </div>
        );
      case 'country':
        return <span title={client.country}>{client.country}</span>;
      case 'group_size':
        // Use conference rooms if active, otherwise use regular year-specific rooms
        if (client.isConferenceRoomsActive && typeof client.conferenceRooms === 'number') {
          return client.conferenceRooms;
        }
        const yearData = getYearSpecificData(client, selectedYear);
        return yearData.rooms;
      case 'conference_manager':
        return <span title={client.conference_manager}>{client.conference_manager}</span>;
      case 'tier':
        return (
          <span 
            className="table__tier-badge" 
            style={{ 
              backgroundColor: useUniformColor ? `${cardColor}20` : `${cardColor}20`, 
              color: cardColor 
            }}
          >
            Tier {clientTier}
          </span>
        );
      case 'revenue':
        // Use conference revenue if active, otherwise use regular year-specific revenue
        const displayRevenue = client.isConferenceRevenueActive 
          ? formatCurrency(client.conferenceRevenue)
          : getYearSpecificData(client, selectedYear).revenue;
        return <span className="table__revenue">{displayRevenue}</span>;
      case 'actions':
        return (
          <div className="table__action-buttons">
            <button 
              className={`btn--lock ${isClientLocked(client) ? 'locked' : ''}`}
              onClick={(e) => {
                e.stopPropagation();
                toggleClientLock(client);
              }}
              title={isClientLocked(client) ? "Unlock client" : "Lock client to top"}
            >
              {isClientLocked(client) ? (
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                  <circle cx="12" cy="16" r="1"></circle>
                  <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
                </svg>
              ) : (
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                  <circle cx="12" cy="16" r="1"></circle>
                  <path d="M7 11V7a5 5 0 0 1 5.9-4.9"></path>
                </svg>
              )}
            </button>
            <button 
              className="btn--unpin"
              onClick={(e) => {
                e.stopPropagation();
                unpinClient(client);
              }}
              title="Unpin client"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill={cardColor} stroke={cardColor} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"></path>
                <circle cx="12" cy="10" r="3"></circle>
              </svg>
            </button>
          </div>
        );
      default:
        return '';
    }
  };

  // Prepare data
  const tableData = prepareTableData();

  return (
    <div className="table-container">
      <table ref={tableRef} className="table">
        <thead>
          <tr>
            {columns.map((column, index) => (
              <th 
                key={column.key}
                style={{ 
                  width: `${columnWidths[column.key]}px`,
                  minWidth: `${columnWidths[column.key]}px`,
                  maxWidth: `${columnWidths[column.key]}px`
                }}
                className={column.sortable ? 'table__sortable-header' : ''}
                onClick={column.sortable ? () => column.onSort(column.key) : undefined}
              >
                <div className="table__header-content">
                  <span className="table__header-text">
                    {column.label}
                    {column.sortable && column.sortIndicator}
                  </span>
                  {index < columns.length - 1 && (
                    <div 
                      className="table__column-resizer"
                      onMouseDown={(e) => handleMouseDown(e, column.key)}
                    />
                  )}
                </div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {tableData.map((item, index) => (
            <tr 
              key={index}
              className={item.className || ''}
            >
              {item.isExpandedRow ? (
                // Expanded row spans all columns
                <td 
                  colSpan={columns.length}
                  style={{ padding: 0, border: 'none' }}
                >
                  {renderTableRowData(item, 'client_name', index)}
                </td>
              ) : (
                // Regular row with individual columns
                columns.map((column) => (
                  <td 
                    key={column.key}
                    style={{ 
                      width: `${columnWidths[column.key]}px`,
                      minWidth: `${columnWidths[column.key]}px`,
                      maxWidth: `${columnWidths[column.key]}px`
                    }}
                  >
                    {renderTableRowData(item, column.key, index)}
                  </td>
                ))
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default ClientTable;
